/**
 * RelationshipEditor - Component for editing relationship type and property mappings in the sidebar
 */

import { Trash2 } from 'lucide-react'
import React, { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { FilterableCombobox } from '@/components/ui/filterable-combobox'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import type { TabularColumn } from '@/services/api/lake'
import type { RelationType } from '@/services/api/schemas'
import { getColumnSamples } from './helpers'
import type { BuiltEntity } from './types'

interface RelationshipEditorProps {
  relationshipType: string
  sourceEntityId: string
  targetEntityId: string
  propertyMappings: Record<string, string>
  relationshipTypes: RelationType[]
  entities: BuiltEntity[]
  availableColumns: string[]
  tabularData: { columns: TabularColumn[]; rows: (string | number | boolean | null)[][] } | null
  onRelationshipTypeChange: (newType: string) => void
  onPropertyMappingChange: (propertyName: string, columnName: string | null) => void
  onDeleteRelationship: () => void
}

export const RelationshipEditor = React.memo(function RelationshipEditor({
  relationshipType,
  sourceEntityId,
  targetEntityId,
  propertyMappings,
  relationshipTypes,
  entities,
  availableColumns,
  tabularData,
  onRelationshipTypeChange,
  onPropertyMappingChange,
  onDeleteRelationship,
}: RelationshipEditorProps) {
  const sourceEntity = entities.find(e => e.id === sourceEntityId)
  const targetEntity = entities.find(e => e.id === targetEntityId)

  // Filter relationship types to only show compatible ones based on source/target entity types
  const compatibleTypes = useMemo(() => {
    const sourceEntityType = sourceEntity?.entityType || ''
    const targetEntityType = targetEntity?.entityType || ''

    return relationshipTypes.filter(rt => {
      if (rt.source_types && rt.source_types.length > 0) {
        if (!rt.source_types.includes(sourceEntityType)) return false
      }
      if (rt.target_types && rt.target_types.length > 0) {
        if (!rt.target_types.includes(targetEntityType)) return false
      }
      return true
    })
  }, [relationshipTypes, sourceEntity?.entityType, targetEntity?.entityType])

  const relTypeDef = relationshipTypes.find(rt => rt.label === relationshipType)

  return (
    <div className="p-4 space-y-4">
      {/* Source → Target Display */}
      <div className="bg-muted/30 rounded-lg p-3">
        <div className="flex items-center gap-2 text-xs">
          <div
            className="px-2 py-1 rounded text-white"
            style={{ backgroundColor: sourceEntity?.color || '#666' }}
          >
            {sourceEntity?.label || 'Unknown'}
          </div>
          <span className="text-muted-foreground">→</span>
          <div
            className="px-2 py-1 rounded text-white"
            style={{ backgroundColor: targetEntity?.color || '#666' }}
          >
            {targetEntity?.label || 'Unknown'}
          </div>
        </div>
      </div>

      {/* Relationship Type Selector */}
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">
          Relationship Type <span className="text-destructive">*</span>
        </Label>
        <FilterableCombobox
          value={relationshipType || '__none__'}
          onValueChange={val => onRelationshipTypeChange(val === '__none__' ? '' : val)}
          items={[
            { value: '__none__', label: '(Select type...)' },
            ...compatibleTypes.map(rt => ({
              value: rt.label,
              label: rt.label,
              description: rt.description,
            })),
          ]}
          placeholder="Select relationship type..."
          searchPlaceholder="Search relationship types..."
        />
        {relationshipType && relTypeDef?.description && (
          <p className="text-xs text-muted-foreground mt-1">{relTypeDef.description}</p>
        )}
      </div>

      {/* Property Mappings - only show when type is selected */}
      {relationshipType && relTypeDef?.properties && relTypeDef.properties.length > 0 && (
        <>
          <Separator />
          <div className="space-y-3">
            <Label className="text-xs font-semibold">Property Mappings</Label>
            {relTypeDef.properties.map(prop => (
              <div key={prop.name} className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {prop.name}
                  {prop.required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
                <FilterableCombobox
                  value={propertyMappings[prop.name] || '__none__'}
                  onValueChange={val => {
                    onPropertyMappingChange(prop.name, val === '__none__' ? null : val)
                  }}
                  items={[
                    { value: '__none__', label: '(unmapped)' },
                    ...availableColumns.map(col => {
                      const samples = tabularData
                        ? getColumnSamples(col, tabularData.columns, tabularData.rows, 2)
                        : []
                      return {
                        value: col,
                        label: col,
                        meta: samples.length > 0 ? `e.g., ${samples.join(', ')}` : undefined,
                      }
                    }),
                  ]}
                  placeholder="Select column..."
                  searchPlaceholder="Search columns..."
                />
                {prop.description && (
                  <p className="text-[10px] text-muted-foreground">{prop.description}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* No properties message */}
      {relationshipType &&
        relTypeDef &&
        (!relTypeDef.properties || relTypeDef.properties.length === 0) && (
          <div className="text-xs text-muted-foreground italic">
            No properties defined for this relationship type
          </div>
        )}

      {/* Delete Relationship Button */}
      <Separator />
      <Button variant="destructive" size="sm" onClick={onDeleteRelationship} className="w-full">
        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
        Delete Relationship
      </Button>
    </div>
  )
})
